package ru.villawonka.app.presentation.screens.search.viewmodel

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import ru.villawonka.app.common.utils.fold
import ru.villawonka.app.domain.model.PaginatedResult
import ru.villawonka.app.domain.model.PostTypeDomain
import ru.villawonka.app.domain.model.SearchParamsDomain
import ru.villawonka.app.domain.model.SortTypeDomain
import ru.villawonka.app.domain.model.groupId
import ru.villawonka.app.domain.model.hasCustomFilters
import ru.villawonka.app.domain.service.FavoritesService
import ru.villawonka.app.domain.service.UserSettingsService
import ru.villawonka.app.domain.usecase.GetUserSettingsUseCase
import ru.villawonka.app.domain.usecase.RequestSearchCategoriesUseCase
import ru.villawonka.app.domain.usecase.RequestSearchResultUseCase
import ru.villawonka.app.presentation.model.PostUIModel
import ru.villawonka.app.presentation.model.groupId
import ru.villawonka.app.presentation.model.toUIModel
import ru.villawonka.app.presentation.screens.search.model.SearchCategoryUIModel
import ru.villawonka.app.presentation.screens.search.model.SortTypeUIModel
import ru.villawonka.app.presentation.screens.search.model.toDomain
import ru.villawonka.app.presentation.screens.search.model.toDomainModel
import ru.villawonka.app.presentation.screens.search.model.toSortTypeUIModel
import ru.villawonka.app.presentation.screens.search.model.toUIModel
import ru.villawonka.app.presentation.screens.searchFilter.compose.SearchFilterScreen
import ru.villawonka.app.presentation.screens.variantPicker.compose.VariantPickerScreen

class SearchViewModel(
    private val requestSearchResultUseCase: RequestSearchResultUseCase,
    private val requestSearchCategoriesUseCase: RequestSearchCategoriesUseCase,
    private val favoritesService: FavoritesService,
    private val userSettingsService: UserSettingsService,
    private val getUserSettingsUseCase: GetUserSettingsUseCase,
) : ScreenModel, UserSettingsService.Listener {

    private val _searchUIEvents = MutableSharedFlow<SearchUIEvent>()
    val searchUIEvents = _searchUIEvents.asSharedFlow()

    private val _searchUIState = MutableStateFlow<SearchUIState>(SearchUIState.Loading)
    val searchUIState = _searchUIState.asStateFlow()

    private val _searchResultUIState = MutableStateFlow<SearchResultsUIState>(SearchResultsUIState.Loading)
    val searchResultUIState = _searchResultUIState.asStateFlow()

    private var cachedPosts: List<PostUIModel> = emptyList()
    private val savedSearchParams = mutableMapOf<PostTypeDomain, SearchParamsDomain>()

    private var isLoadingPosts = false
    private var selectedSortType: SortTypeDomain = SortTypeDomain.NEWEST
    private var selectedSearchCategory: PostTypeDomain = PostTypeDomain.ACCOMMODATION
    private val selectedSearchParams: SearchParamsDomain
        get() = savedSearchParams[selectedSearchCategory] ?: run {
            val defaultSearchParamsDomain = when(selectedSearchCategory) {
                PostTypeDomain.ACCOMMODATION -> SearchParamsDomain.RentalItem.Accommodation()
                PostTypeDomain.TRANSPORT -> SearchParamsDomain.RentalItem.Transport()
                PostTypeDomain.MONEY_EXCHANGE,
                PostTypeDomain.VISA_AGENCY -> throw NotImplementedError()
            }
            savedSearchParams[selectedSearchCategory] = defaultSearchParamsDomain
            defaultSearchParamsDomain
        }

    init {
        loadScreen()
        observeToggleLikeEvents()
        userSettingsService.addDelegate(this)
    }

    fun onLoadSimilar(post: PostUIModel) = screenModelScope.launch {
        _searchResultUIState.value.asData()?.let { state ->
            _searchResultUIState.emit(state.copy(
                posts = state.posts.map {
                    if (it is PostUIModel.Accommodation) {
                        if (it.id == post.id) it.copy(similarCount = 0)
                        else if (it.groupId() == post.groupId()) it.copy(hidden = false)
                        else it
                    } else it
                }
            ))
        }
    }

    private fun observeToggleLikeEvents() = screenModelScope.launch {
        favoritesService.toggleLikeEvents.onEach { (postId, isLiked) ->
            _searchResultUIState.value.asData()?.let { state ->
                _searchResultUIState.emit(state.copy(
                    posts = state.posts.map { currentPost ->
                        if (currentPost.id == postId) {
                            when (currentPost) {
                                is PostUIModel.Accommodation -> {
                                    currentPost.copy(isLiked = isLiked)
                                }
                            }
                        } else {
                            currentPost
                        }
                    }
                ))
            }
        }.launchIn(this)
    }

    override fun onDispose() {
        userSettingsService.removeDelegate(this)
        super.onDispose()
    }

    override fun onSettingsChanged() { loadSearchResults(true) }

    fun onRetryButtonClick() { loadScreen() }

    fun onMapPinButtonClick() = screenModelScope.launch {
        _searchUIEvents.emit(SearchUIEvent.OpenCountrySelectScreen)
    }

    fun onSettingsButtonClick() = screenModelScope.launch {
        _searchUIEvents.emit(SearchUIEvent.OpenSettingsScreen)
    }

    fun onTabSelected(newSelectedSearchCategory: SearchCategoryUIModel) = screenModelScope.launch {
        _searchUIState.value.asData()?.let { state ->
            _searchUIState.emit(state.copy(
                selectedSearchCategory = newSelectedSearchCategory,
                hasCustomFilters = selectedSearchParams.hasCustomFilters
            ))
            selectedSearchCategory = newSelectedSearchCategory.toDomain()
        }
    }

    fun onSearchFilterButtonClicked() = screenModelScope.launch {
        _searchUIEvents.emit(
            SearchUIEvent.ShowFilterScreen(searchParamsDomain = selectedSearchParams)
        )
    }

    fun onSearchSortButtonClicked() = screenModelScope.launch {
        _searchUIState.value.asData()?.let { state ->
            _searchUIEvents.emit(SearchUIEvent.ShowSortScreen(
                selectedItem = state.selectedSortType,
                items = listOf(
                    SortTypeUIModel.Newest(),
                    SortTypeUIModel.HighPrice(),
                    SortTypeUIModel.LowPrice(),
                )
            ))
        }
    }

    fun onSearchSortScreenResult(result: VariantPickerScreen.Params.Output) = screenModelScope.launch {
        _searchUIState.value.asData()?.let { state ->
            result.selectedItem.toSortTypeUIModel()?.let { sortType ->
                selectedSortType = sortType.toDomainModel()
                _searchUIState.emit(state.copy(selectedSortType = sortType))
                _searchUIEvents.emit(SearchUIEvent.ScrollToUp)
                loadSearchResults(true)
            }
        }
    }

    fun onSearchFilterScreenResult(result: SearchFilterScreen.Params.Output) = screenModelScope.launch {
        _searchUIState.value.asData()?.let { state ->
            savedSearchParams[selectedSearchCategory] = result.searchParamsDomain
            _searchUIState.emit(state.copy(
                hasCustomFilters = result.searchParamsDomain.hasCustomFilters
            ))
            loadSearchResults(true)
        }
    }

    suspend fun onLoadNextPage() {
        loadSearchResults(false).join()
    }

    private fun loadScreen() = screenModelScope.launch {
        _searchUIState.emit(SearchUIState.Loading)

        getUserSettingsUseCase().fold(
            ifLeft = { _searchUIState.emit(SearchUIState.Error(it)) },
            ifRight = { userSettings ->
                val allSearchCategories = requestSearchCategoriesUseCase()
                selectedSearchCategory = allSearchCategories.first()

                _searchUIState.emit(SearchUIState.Data(
                    country = userSettings.country.toUIModel(),
                    selectedSortType = selectedSortType.toUIModel(),
                    selectedSearchCategory = selectedSearchCategory.toUIModel(),
                    allSearchCategories = allSearchCategories.map { it.toUIModel() },
                    hasCustomFilters = selectedSearchParams.hasCustomFilters,
                ))

                loadSearchResults(true)
            }
        )
    }

    private fun loadSearchResults(resetPagination: Boolean) = screenModelScope.launch {
        if (isLoadingPosts) return@launch else isLoadingPosts = true

        if (resetPagination) {
            _searchResultUIState.emit(SearchResultsUIState.Loading)
        }

        val searchResultState = requestSearchResultUseCase(
            searchParams = selectedSearchParams,
            sortType = selectedSortType,
            resetPagination = resetPagination,
        ).fold(
            ifLeft = {
                when (it) {
                    is PaginatedResult.Failure.Network -> {
                        SearchResultsUIState.Error(it.failure)
                    }
                    is PaginatedResult.Failure.NoMoreData -> {
                        _searchResultUIState.value.asData()?.copy(hasMoreData = false)
                    }
                }
            },
            ifRight = { posts ->
                if (resetPagination) cachedPosts = emptyList()
                if (posts.isEmpty()) {
                    SearchResultsUIState.Empty
                } else {
                    val groups = posts.groupBy { it.groupId() }

                    cachedPosts = cachedPosts.plus(
                        elements = posts.map { post ->
                            val groupPosts = groups[post.groupId()] ?: emptyList()
                            val isMainPost = groupPosts.firstOrNull() == post // Главный - первый в группе

                            post.toUIModel(
                                hidden = !isMainPost && groupPosts.size > 1, // Скрываем неглавные в группах
                                similarCount = if (isMainPost) groupPosts.size - 1 else 0,
                                isMain = isMainPost
                            )
                        }
                    )

                    SearchResultsUIState.Data(
                        posts = cachedPosts,
                        hasMoreData = true
                    )
                }
            }
        )

        searchResultState?.let {
            _searchResultUIState.emit(searchResultState)
        }
        isLoadingPosts = false
    }
}